import { useState, useCallback, useEffect, type CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AvailableTransition, EntityRecord, EntityTypeDefinition } from "../../../lib/types";
import type { TableColumn } from "../../lib/v3-columns";
import { FieldCell } from "./FieldCell";
import { CellEditor } from "./CellEditor";
import { AddColumnModal } from "./AddColumnModal";
import { ColumnHeaderMenu } from "./ColumnHeaderMenu";
import { fieldIsModuleOwned, typeModuleOwner, moduleLabel } from "../../lib/module-ownership";
import { ConfirmDeleteColumnModal } from "./ConfirmDeleteColumnModal";
import { RowActionMenu } from "./RowActionMenu";
import { useRowDetailPanel } from "../entity/RowDetailContext";
import { useUpdateEntityType } from "../../../lib/hooks/use-kuvio";
import { queryKeys } from "../../../lib/query-keys";
import { showToast } from "../primitives";

const RESERVED_COLUMN_NAMES = ["id", "_meta", "created_at", "updated_at"];

/** Mirror AddColumnModal's slugify so renamed columns produce valid field names. */
function toFieldSlug(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
}

function resolveFieldValue(row: EntityRecord, field: string): unknown {
  if (field.includes(".")) {
    const parts = field.split(".");
    let current: unknown = row;
    for (const part of parts) {
      if (current == null || typeof current !== "object") return undefined;
      current = (current as Record<string, unknown>)[part];
    }
    return current;
  }
  return row[field];
}

/** Compact relative time for the wrapped-API sync indicator ("2m ago", "3h ago"). */
function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.parse(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(ms)) return "";
  const diff = Math.max(0, Date.now() - ms) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * Sprint 16c (S16C.29/30) — subtle origin badge on materialised rows. Shows the
 * wrapped-API plug icon + last-synced relative time; the title carries the full
 * provenance. Native (non-wrapped) rows render nothing.
 */
function WrappedSyncBadge({ wrapper, syncedAt }: { wrapper: string; syncedAt: string | null }) {
  const rel = relativeTime(syncedAt);
  return (
    <span
      title={`Materialised from "${wrapper}"${rel ? ` · synced ${rel}` : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 10,
        color: "var(--color-text-tertiary)",
        fontFamily: "var(--font-sans)",
        whiteSpace: "nowrap",
      }}
    >
      <i className="ti ti-plug-connected" style={{ fontSize: 12 }} aria-hidden="true" />
      {rel && <span>{rel}</span>}
    </span>
  );
}

type SortDir = "asc" | "desc";

interface SortState {
  column: string;
  dir: SortDir;
}

interface EditingCell {
  rowId: string;
  field: string;
}

interface TableGridProps {
  columns: TableColumn[];
  rows: EntityRecord[];
  entityType: EntityTypeDefinition;
  sort: SortState | null;
  onSortChange: (sort: SortState) => void;
  onCellSave: (rowId: string, field: string, value: unknown) => void;
  mutatingRows: Set<string>;
  onFilterColumn?: (column: string) => void;
  newRowId?: string | null;
  onColumnAdded?: () => void;
  emptyState?: React.ReactNode;
  /** Sprint 12 — resolve FK ids to display values for inline reference chips. */
  resolveRef?: (id: string) => string | undefined;
}

export function TableGrid({
  columns,
  rows,
  entityType,
  sort,
  onSortChange,
  onCellSave,
  mutatingRows,
  onFilterColumn,
  newRowId,
  onColumnAdded,
  emptyState,
  resolveRef,
}: TableGridProps) {
  const { openPanel } = useRowDetailPanel();
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    column: TableColumn;
  } | null>(null);

  // Sprint 14a — column admin (inline rename, delete-with-confirm, drag reorder).
  // All three route through update_entity_type with a rewritten `fields` array.
  const updateEntityType = useUpdateEntityType();
  const qc = useQueryClient();
  const [renamingField, setRenamingField] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TableColumn | null>(null);
  const [dragField, setDragField] = useState<string | null>(null);
  const [dropField, setDropField] = useState<string | null>(null);

  // A column is admin-editable only when it maps to a real entity-type field;
  // the virtual id/created columns are synthesised and stay fixed.
  const fieldNames = new Set(entityType.fields.map((f) => f.name));
  const isSchemaColumn = (col: TableColumn) => fieldNames.has(col.field);

  const sortedRows = sortRows(rows, sort, columns);

  // Sprint 12b — the ID column (the system-managed, uneditable first column) is
  // the dedicated side-panel trigger. Identify it once: prefer the virtual id
  // column / a field literally named "id", else fall back to the first column.
  const idField =
    columns.find((c) => c.type === "virtual-id" || c.field === "id")?.field ??
    columns[0]?.field;

  // Sprint 12b — workflow-aware inline editing. When the type has a workflow,
  // the editor for its state field greys out transitions the server says aren't
  // currently reachable (per each row's `_transitions`).
  const workflowField = entityType.workflow ? entityType.workflow.field ?? "status" : undefined;

  useEffect(() => {
    if (!newRowId) return;
    const titleCol = columns.find((c) => c.isTitle && c.editable);
    if (titleCol && !editing) {
      setEditing({ rowId: newRowId, field: titleCol.field });
    }
  }, [newRowId, columns]);

  function handleHeaderClick(col: TableColumn) {
    if (sort?.column === col.field) {
      onSortChange({ column: col.field, dir: sort.dir === "asc" ? "desc" : "asc" });
    } else {
      onSortChange({ column: col.field, dir: "asc" });
    }
  }

  function handleHeaderContextMenu(e: React.MouseEvent, col: TableColumn) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, column: col });
  }

  function beginRename(col: TableColumn) {
    setRenameError(null);
    setRenameDraft(col.label);
    setRenamingField(col.field);
  }

  function cancelRename() {
    setRenamingField(null);
    setRenameError(null);
  }

  function commitRename(col: TableColumn) {
    if (updateEntityType.isPending) return;
    const slug = toFieldSlug(renameDraft);
    if (!slug) {
      setRenameError("Name can't be empty.");
      return;
    }
    if (slug === col.field) {
      cancelRename();
      return;
    }
    if (RESERVED_COLUMN_NAMES.includes(slug)) {
      setRenameError(`'${slug}' is reserved.`);
      return;
    }
    if (fieldNames.has(slug)) {
      setRenameError("A column with this name already exists.");
      return;
    }
    const fields = entityType.fields.map((f) => (f.name === col.field ? { ...f, name: slug } : f));
    updateEntityType.mutate(
      { slug: entityType.slug, patch: { fields }, expectedVersion: entityType._version },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: queryKeys.entities(entityType.slug) });
          onColumnAdded?.();
          setRenamingField(null);
          showToast("Column renamed");
        },
        onError: (e) => setRenameError(e instanceof Error ? e.message : "Rename failed"),
      },
    );
  }

  function doDeleteColumn(col: TableColumn): Promise<void> {
    const fields = entityType.fields.filter((f) => f.name !== col.field);
    return updateEntityType
      .mutateAsync({ slug: entityType.slug, patch: { fields }, expectedVersion: entityType._version })
      .then(() => {
        qc.invalidateQueries({ queryKey: queryKeys.entities(entityType.slug) });
        onColumnAdded?.();
        showToast(`Column "${col.label}" deleted`);
      });
  }

  function handleColDragStart(e: React.DragEvent, col: TableColumn) {
    setDragField(col.field);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", col.field);
    } catch {
      /* some browsers disallow setData outside a user gesture — safe to ignore */
    }
  }

  function handleColDragOver(e: React.DragEvent, col: TableColumn) {
    if (!dragField || dragField === col.field || !isSchemaColumn(col)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropField !== col.field) setDropField(col.field);
  }

  function handleColDragEnd() {
    setDragField(null);
    setDropField(null);
  }

  function handleColDrop(e: React.DragEvent, col: TableColumn) {
    e.preventDefault();
    const from = dragField;
    setDropField(null);
    setDragField(null);
    if (!from || from === col.field) return;
    // Move the dragged field to the target field's slot inside the full fields
    // array — hidden/document fields (absent from the grid) keep their relative
    // positions and just shift around the move.
    const fields = [...entityType.fields];
    const fromIdx = fields.findIndex((f) => f.name === from);
    const toIdx = fields.findIndex((f) => f.name === col.field);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = fields.splice(fromIdx, 1);
    if (!moved) return;
    fields.splice(toIdx, 0, moved);
    updateEntityType.mutate(
      { slug: entityType.slug, patch: { fields }, expectedVersion: entityType._version },
      {
        onSuccess: () => onColumnAdded?.(),
        onError: (err) => showToast(err instanceof Error ? err.message : "Reorder failed", "error", true),
      },
    );
  }

  function toggleDisplayField(col: TableColumn) {
    const isAlready = entityType.fields.find((f) => f.name === col.field)?.display_field;
    const fields = entityType.fields.map((f) => {
      const copy = { ...f };
      if (f.name === col.field) {
        copy.display_field = !isAlready;
      } else if (!isAlready) {
        delete copy.display_field;
      }
      return copy;
    });
    updateEntityType.mutate(
      { slug: entityType.slug, patch: { fields }, expectedVersion: entityType._version },
      {
        onSuccess: () => {
          onColumnAdded?.();
          showToast(isAlready ? "Display field removed" : `"${col.label}" set as display field`);
        },
        onError: (err) => showToast(err instanceof Error ? err.message : "Update failed", "error", true),
      },
    );
  }

  function handleCellSave(rowId: string, field: string, value: unknown) {
    onCellSave(rowId, field, value);
    setEditing(null);
  }

  const handleTab = useCallback(
    (rowId: string, field: string, shift: boolean) => {
      const editableCols = columns.filter((c) => c.editable);
      const colIdx = editableCols.findIndex((c) => c.field === field);
      const rowIdx = sortedRows.findIndex((r) => r.id === rowId);

      if (shift) {
        if (colIdx > 0) {
          const prevCol = editableCols[colIdx - 1];
          if (prevCol) setEditing({ rowId, field: prevCol.field });
        } else if (rowIdx > 0) {
          const prevRow = sortedRows[rowIdx - 1];
          const lastCol = editableCols[editableCols.length - 1];
          if (prevRow && lastCol) setEditing({ rowId: prevRow.id, field: lastCol.field });
        }
      } else {
        if (colIdx < editableCols.length - 1) {
          const nextCol = editableCols[colIdx + 1];
          if (nextCol) setEditing({ rowId, field: nextCol.field });
        } else if (rowIdx < sortedRows.length - 1) {
          const nextRow = sortedRows[rowIdx + 1];
          const firstCol = editableCols[0];
          if (nextRow && firstCol) setEditing({ rowId: nextRow.id, field: firstCol.field });
        }
      }
    },
    [columns, sortedRows],
  );

  function handleRowOpen(e: React.MouseEvent, rowId: string) {
    // Cmd/Ctrl-click opens the shareable full page in a new tab; a plain click
    // opens the slide-in detail panel without leaving the table.
    if (e.metaKey || e.ctrlKey) {
      window.open(`/entities/${entityType.slug}/${rowId}`, "_blank", "noopener");
    } else {
      openPanel(entityType.slug, rowId);
    }
  }

  // Close context menu on click anywhere
  if (contextMenu) {
    const closeMenu = () => setContextMenu(null);
    document.addEventListener("mousedown", closeMenu, { once: true });
  }

  const tableStyle: CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    fontFamily: "var(--font-sans)",
  };

  const thStyle = (col: TableColumn): CSSProperties => ({
    textAlign: col.align,
    padding: "8px 10px",
    fontWeight: 500,
    fontSize: 11,
    color: "var(--color-text-tertiary)",
    borderBottom: "0.5px solid var(--color-border-tertiary)",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  });

  const renameInputStyle: CSSProperties = {
    width: "100%",
    minWidth: 90,
    fontSize: 11,
    fontWeight: 500,
    padding: "2px 4px",
    border: "1px solid var(--kuvio-cell-edit-border)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };

  // Impact preview for the delete-column confirmation.
  const deleteRowCount = rows.length;
  const deleteNonEmptyCount = deleteTarget
    ? rows.filter((r) => {
        const v = resolveFieldValue(r, deleteTarget.field);
        return v !== null && v !== undefined && v !== "";
      }).length
    : 0;

  return (
    <div
      style={{
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: "var(--border-radius-md)",
        overflowX: "auto",
      }}
    >
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: "var(--color-background-secondary)" }}>
            {columns.map((col) => {
              const schema = isSchemaColumn(col);
              const isRenaming = renamingField === col.field;
              return (
                <th
                  key={col.field}
                  className={`kuvio-col-header${dropField === col.field ? " kuvio-col-drop-target" : ""}`}
                  style={{ ...thStyle(col), minWidth: col.minWidth }}
                  onContextMenu={(e) => handleHeaderContextMenu(e, col)}
                  onDragOver={schema ? (e) => handleColDragOver(e, col) : undefined}
                  onDrop={schema ? (e) => handleColDrop(e, col) : undefined}
                >
                  {isRenaming ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => {
                          setRenameDraft(e.target.value);
                          if (renameError) setRenameError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitRename(col);
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelRename();
                          }
                        }}
                        onBlur={() => commitRename(col)}
                        aria-label={`Rename column ${col.label}`}
                        style={renameInputStyle}
                      />
                      {renameError && (
                        <div style={{ fontSize: 10, fontWeight: 400, color: "var(--c-red-600)", marginTop: 2 }}>
                          {renameError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {schema && (
                        <span
                          className="kuvio-col-grip"
                          draggable
                          onClick={(e) => e.stopPropagation()}
                          onDragStart={(e) => handleColDragStart(e, col)}
                          onDragEnd={handleColDragEnd}
                          title="Drag to reorder"
                          style={{ display: "inline-flex", marginLeft: -2 }}
                          aria-hidden="true"
                        >
                          <i className="ti ti-grip-vertical" style={{ fontSize: 13 }} />
                        </span>
                      )}
                      <span
                        onClick={() => handleHeaderClick(col)}
                        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        {col.label}
                        {schema && entityType.fields.find((f) => f.name === col.field)?.display_field && (
                          <i
                            className="ti ti-star"
                            style={{ fontSize: 10, color: "var(--c-amber-500)" }}
                            title="Display field"
                            aria-hidden="true"
                          />
                        )}
                        {sort?.column === col.field && (
                          <i
                            className={`ti ti-arrow-${sort.dir === "asc" ? "up" : "down"}`}
                            style={{ fontSize: 11 }}
                            aria-hidden="true"
                          />
                        )}
                      </span>
                      {schema && (
                        <span style={{ marginLeft: "auto", display: "inline-flex" }}>
                          <ColumnHeaderMenu
                            onRename={() => beginRename(col)}
                            onDelete={() => setDeleteTarget(col)}
                            isDisplayField={!!entityType.fields.find((f) => f.name === col.field)?.display_field}
                            onToggleDisplayField={() => toggleDisplayField(col)}
                            locked={fieldIsModuleOwned(entityType.fields.find((f) => f.name === col.field)?.origin)}
                            lockedLabel={(() => {
                              const o = typeModuleOwner([entityType.fields.find((f) => f.name === col.field)!].filter(Boolean));
                              return o ? `Part of ${moduleLabel(o)}` : undefined;
                            })()}
                          />
                        </span>
                      )}
                    </div>
                  )}
                </th>
              );
            })}
            <th
              style={{
                padding: "6px 10px",
                borderBottom: "0.5px solid var(--color-border-tertiary)",
                whiteSpace: "nowrap",
              }}
            >
              <button
                onClick={() => setAddColumnOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  padding: "4px 8px",
                  border: "var(--kuvio-border-default)",
                  background: "transparent",
                  borderRadius: "var(--border-radius-sm)",
                  color: "var(--color-text-tertiary)",
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" />
                Add column
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 && emptyState && (
            <tr>
              <td
                colSpan={columns.length + 1}
                style={{
                  background: "var(--color-background-secondary)",
                  textAlign: "center",
                  padding: "24px 16px",
                }}
              >
                {emptyState}
              </td>
            </tr>
          )}
          {sortedRows.map((row, rowIdx) => {
            const isMutating = mutatingRows.has(row.id);
            const isLast = rowIdx === sortedRows.length - 1;

            const rowStyle: CSSProperties = {
              borderBottom: isLast ? "none" : "0.5px solid var(--color-border-tertiary)",
              background: isMutating ? "var(--kuvio-row-mutating-bg)" : "transparent",
              transition: "background 200ms ease",
              // Sprint 12b — the row no longer opens the panel on any-cell click;
              // the ID column owns that intent (its own cursor + chevron).
              cursor: "default",
            };

            const titleCol = columns.find((c) => c.isTitle);
            const rowTitle = titleCol
              ? String(resolveFieldValue(row, titleCol.field) ?? "")
              : undefined;

            return (
              <tr key={row.id} style={rowStyle} className="kuvio-table-row">
                {columns.map((col) => {
                  const isEditing =
                    editing?.rowId === row.id && editing?.field === col.field;

                  if (isEditing) {
                    const isWorkflowField =
                      !!workflowField && col.field === workflowField;
                    return (
                      <CellEditor
                        key={col.field}
                        value={resolveFieldValue(row, col.field)}
                        column={col}
                        onSave={(val) => handleCellSave(row.id, col.field, val)}
                        onCancel={() => setEditing(null)}
                        onTab={(shift) => handleTab(row.id, col.field, shift)}
                        workflowState={
                          isWorkflowField
                            ? {
                                field: workflowField!,
                                current: String(resolveFieldValue(row, workflowField!) ?? ""),
                                transitions: (row._transitions ?? []) as AvailableTransition[],
                              }
                            : undefined
                        }
                      />
                    );
                  }

                  return (
                    <FieldCell
                      key={col.field}
                      value={resolveFieldValue(row, col.field)}
                      column={col}
                      isEditing={false}
                      isMutating={isMutating}
                      resolveRef={resolveRef}
                      isIdColumn={col.field === idField}
                      onOpenDetail={(e) => handleRowOpen(e, row.id)}
                      onStartEdit={() => setEditing({ rowId: row.id, field: col.field })}
                    />
                  );
                })}
                <td style={{ padding: "0 4px", whiteSpace: "nowrap", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                    {row._meta?.created_via_wrapper && (
                      <WrappedSyncBadge
                        wrapper={row._meta.created_via_wrapper}
                        syncedAt={row._meta.last_synced_at ?? null}
                      />
                    )}
                    <div className="kuvio-row-action">
                      <RowActionMenu
                        rowId={row.id}
                        rowTitle={rowTitle}
                        entityType={entityType}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <AddColumnModal
        open={addColumnOpen}
        onOpenChange={setAddColumnOpen}
        entityType={entityType}
        onAdded={onColumnAdded}
      />

      <ConfirmDeleteColumnModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        column={deleteTarget}
        rowCount={deleteRowCount}
        nonEmptyCount={deleteNonEmptyCount}
        onConfirm={async () => {
          if (!deleteTarget) return;
          await doDeleteColumn(deleteTarget);
          setDeleteTarget(null);
        }}
      />

      {contextMenu && (
        <div
          role="menu"
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            minWidth: 180,
            background: "var(--color-background-primary)",
            border: "var(--kuvio-border-default)",
            borderRadius: "var(--border-radius-md)",
            padding: 4,
            zIndex: 100,
            fontFamily: "var(--font-sans)",
          }}
        >
          <ContextMenuItem
            label="Sort ascending"
            icon="ti-arrow-up"
            onClick={() => {
              onSortChange({ column: contextMenu.column.field, dir: "asc" });
              setContextMenu(null);
            }}
          />
          <ContextMenuItem
            label="Sort descending"
            icon="ti-arrow-down"
            onClick={() => {
              onSortChange({ column: contextMenu.column.field, dir: "desc" });
              setContextMenu(null);
            }}
          />
          {(contextMenu.column.type === "enum" ||
            contextMenu.column.type === "reference") &&
            onFilterColumn && (
              <ContextMenuItem
                label="Filter by this column"
                icon="ti-filter"
                onClick={() => {
                  onFilterColumn!(contextMenu.column.field);
                  setContextMenu(null);
                }}
              />
            )}
        </div>
      )}
    </div>
  );
}

function ContextMenuItem({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: string;
  onClick: () => void;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "6px 10px",
        border: "none",
        background: "transparent",
        fontSize: 13,
        textAlign: "left",
        borderRadius: "var(--border-radius-sm)",
        color: "var(--color-text-primary)",
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
      }}
    >
      <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden="true" />
      {label}
    </button>
  );
}

function sortRows(
  rows: EntityRecord[],
  sort: SortState | null,
  columns: TableColumn[],
): EntityRecord[] {
  if (!sort) return rows;

  const col = columns.find((c) => c.field === sort.column);
  if (!col) return rows;

  return [...rows].sort((a, b) => {
    const aVal = resolveFieldValue(a, sort.column);
    const bVal = resolveFieldValue(b, sort.column);

    let cmp = compareValues(aVal, bVal, col);
    if (cmp === 0) {
      cmp = (a._meta?.created_at ?? "").localeCompare(b._meta?.created_at ?? "");
    }

    return sort.dir === "desc" ? -cmp : cmp;
  });
}

function compareValues(a: unknown, b: unknown, col: TableColumn): number {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : -1;
  if (b === null || b === undefined) return 1;

  if (col.type === "number" || col.type === "currency") {
    return (Number(a) || 0) - (Number(b) || 0);
  }

  if (col.type === "date" || col.type === "datetime" || col.type === "virtual-timestamp") {
    return new Date(String(a)).getTime() - new Date(String(b)).getTime();
  }

  return String(a).localeCompare(String(b));
}
